import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ToastType } from "@/enums";
import { useDuplicateSmartTable } from "@/queries/smart-tables";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface DuplicateSmartTableDialogProps {
  open: boolean;
  tableId: string | null;
  tableTitle?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const INCLUDE_COMPUTED_CHECKBOX_ID = "duplicate-smart-table-include-computed";

const DuplicateSmartTableDialog = ({
  open,
  tableId,
  tableTitle,
  onClose,
  onSuccess,
}: DuplicateSmartTableDialogProps) => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const duplicateMutation = useDuplicateSmartTable();
  const [includeComputedValues, setIncludeComputedValues] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !duplicateMutation.isPending) {
      setIncludeComputedValues(false);
      onClose();
    }
  };

  const handleDuplicate = async () => {
    if (!tableId) {
      return;
    }
    try {
      const duplicatedTable = await duplicateMutation.mutateAsync({
        tableId,
        includeComputedValues,
      });
      displayToast("Table duplicated", ToastType.success);
      setIncludeComputedValues(false);
      onClose();
      onSuccess?.();
      navigate(`/workspace/${workspaceId}/smart-tables/${duplicatedTable.id}`);
    } catch (error) {
      displayErrorToast(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="text-md break-all font-semibold">
            Duplicate {tableTitle ? `"${tableTitle}"` : "table"}
          </DialogTitle>
          <DialogDescription>
            Creates a copy in the same folder with all sheets, columns, and inputs.
          </DialogDescription>
        </DialogHeader>
        <label
          htmlFor={INCLUDE_COMPUTED_CHECKBOX_ID}
          className="flex cursor-pointer items-center gap-2 text-sm"
        >
          <Checkbox
            id={INCLUDE_COMPUTED_CHECKBOX_ID}
            checked={includeComputedValues}
            onClick={() => setIncludeComputedValues(value => !value)}
            disabled={duplicateMutation.isPending}
            className="h-4 w-4"
          />
          <span>Also copy computed results</span>
        </label>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={duplicateMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleDuplicate} disabled={!tableId || duplicateMutation.isPending}>
            {duplicateMutation.isPending ? <LoadingSpinner size={5} /> : "Duplicate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateSmartTableDialog;
